import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription } from 'rxjs'
import { DhlSettings, DhlSettingsService } from './dhl-settings.service'
import { getCityOptions, getCountryOptions, MAJOR_SA_CITIES } from '../shared/origin-location.util'

const DHL_CREDENTIAL_FIELDS: (keyof DhlSettings)[] = ['apiKey', 'apiSecret']

@Component({
  selector: 'app-dhl-provider',
  templateUrl: './dhl-provider.component.html',
  styleUrls: ['../../../integrations/integrations.component.scss', '../shipping-providers-theme.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DhlProviderComponent implements OnInit, OnDestroy {
  @Input() projectId!: string
  @Input() isOpen = false
  @Output() openPanel = new EventEmitter<void>()
  @Output() closePanel = new EventEmitter<void>()
  @Output() saved = new EventEmitter<void>()

  settings: DhlSettings = {
    enabled: false,
    apiKey: '',
    apiSecret: '',
    accountNumber: '',
    originCountryCode: '',
    originCityName: '',
    originPostalCode: '',
  }

  // DHL Express has no server-side country restriction (it's a global
  // carrier — see docs/shipping/dhl-integration-plan.md, "Covers Egypt,
  // Saudi Arabia, UAE, and all MENA countries"), so the picker offers every
  // country rather than a fixed allowlist like Aramex/SMSA/Aymakan.
  countries = getCountryOptions()

  originCities: string[] = []

  private statusSub?: Subscription

  constructor(public readonly service: DhlSettingsService) {}

  ngOnInit(): void {
    this.statusSub = this.service.status$.subscribe((status) => {
      if (!status?.connected) return
      this.settings.enabled = status.enabled
      this.settings.originCountryCode = status.originCountryCode ?? this.settings.originCountryCode
      this.settings.originCityName = status.originCityName ?? this.settings.originCityName
      this.settings.originPostalCode = status.originPostalCode ?? this.settings.originPostalCode
      this.service.maskCredentials(this.settings, DHL_CREDENTIAL_FIELDS)
      this.refreshOriginCities()
    })
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe()
  }

  /**
   * Repopulates the Origin City options for the currently selected origin
   * country. Keeps an already-saved city selected even if it isn't in the
   * list, so switching countries never silently drops existing data.
   */
  onOriginCountryChange(): void {
    this.settings.originCityName = ''
    this.refreshOriginCities()
  }

  private refreshOriginCities(): void {
    const countryIsoCode = this.settings.originCountryCode
    const curated = countryIsoCode === 'SA' ? MAJOR_SA_CITIES : undefined
    this.originCities = getCityOptions(countryIsoCode, this.settings.originCityName, curated)
  }

  /**
   * Saves encrypted DHL configuration through stacks-agents.
   * Strips placeholder values so the backend preserves existing credentials.
   */
  async save(): Promise<void> {
    const settingsToSave = { ...this.settings }
    for (const field of DHL_CREDENTIAL_FIELDS) {
      if (settingsToSave[field] === this.service.CREDENTIAL_PLACEHOLDER) {
        ;(settingsToSave as Record<string, unknown>)[field] = ''
      }
    }
    await this.service.save(this.projectId, settingsToSave)
    this.saved.emit()
  }

  /**
   * Disables DHL through stacks-agents while keeping credential handling
   * inside the backend.
   */
  disconnect(): void {
    this.service.disconnect(this.projectId)
  }
}
